import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Loader2, Banknote, Pencil, X, Check, Paperclip } from "lucide-react";
import { documentAPI } from "@/lib/api";
import { toast } from "sonner";
import { DateField } from "@/components/ui/date-field";
import { isChequeMode, usePaymentMethods } from "@/lib/paymentMethods";

function formatEur(v) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);
}

// Accepte la virgule française : « 30,50 » -> 30.5
function toNum(v) {
  const n = parseFloat(String(v ?? "").trim().replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function ChequeUpload({ chequeFile, setChequeFile }) {
  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
      toast.error("Image ou PDF uniquement");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 5 Mo)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = String(reader.result || "").split(",")[1] || "";
      setChequeFile({ name: file.name, mime: file.type, base64, size: file.size });
    };
    reader.readAsDataURL(file);
  };
  return (
    <div className="mt-2 rounded-md border border-dashed border-indigo-300 bg-indigo-50/40 px-2.5 py-2" data-testid="cheque-upload-zone">
      <div className="flex items-center gap-2">
        <Paperclip size={12} className="text-indigo-600 shrink-0" />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-indigo-700 shrink-0">Photo / PDF du chèque (optionnel)</span>
        {chequeFile ? (
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <span className="text-xs text-slate-700 truncate" title={chequeFile.name}>{chequeFile.name}</span>
            <span className="text-[10px] text-slate-400">{(chequeFile.size / 1024).toFixed(0)} Ko</span>
            <button type="button" onClick={() => setChequeFile(null)} className="text-slate-400 hover:text-red-500 ml-auto" data-testid="cheque-clear-btn">
              <X size={12} />
            </button>
          </div>
        ) : (
          <label className="ml-auto cursor-pointer text-[11px] text-indigo-700 hover:text-indigo-900 underline" data-testid="cheque-upload-btn">
            Joindre une image ou un PDF
            <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onPick} />
          </label>
        )}
      </div>
    </div>
  );
}

export default function PaymentDialog({ open, onOpenChange, document, clientName, onUpdated }) {
  const [payments, setPayments] = useState([]);
  const [totalPaid, setTotalPaid] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [avoirInfo, setAvoirInfo] = useState({});
  const [totalDue, setTotalDue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [form, setForm] = useState({ date: new Date().toISOString().split("T")[0], mode: "", amount_ttc: "", comment: "" });
  const [chequeFile, setChequeFile] = useState(null);
  const pm = usePaymentMethods();
  const newMode = form.mode || pm.defaultValue;

  const isSolde = document?.subtype === "solde";
  // Le montant dû est le TTC du document moins les acomptes déjà émis : même
  // calcul que le serveur (acomptes.due_amount), jamais le champ figé
  // net_to_pay_ttc, qui ne suit pas une correction des lignes.
  const acompteDeduit = (document?.subtype === "solde" ? (document?.acompte_deductions || []) : [])
    .reduce((s, a) => s + Number(a.amount_ttc || 0), 0);
  const documentTotal = Math.round(((document?.total_ttc || 0) - acompteDeduit) * 100) / 100;
  const displayTotal = totalDue > 0 ? totalDue : documentTotal;

  const loadPayments = useCallback(() => {
    if (!document?.id) return;
    setLoading(true);
    documentAPI.listPayments(document.id)
      .then(r => {
        setPayments(r.data.payments || []);
        setTotalPaid(r.data.total_paid || 0);
        setRemaining(r.data.remaining || 0);
        setTotalDue(r.data.total_due || r.data.total_ttc || 0);
        setAvoirInfo({
          avoir_imputed_ttc: r.data.avoir_imputed_ttc || 0,
          avoir_draft_ttc: r.data.avoir_draft_ttc || 0,
          avoir_draft_count: r.data.avoir_draft_count || 0,
          avoir_refund_ttc: r.data.avoir_refund_ttc || 0,
          avoir_refund_count: r.data.avoir_refund_count || 0,
        });
      })
      .catch(() => toast.error("Erreur chargement"))
      .finally(() => setLoading(false));
  }, [document?.id]);

  useEffect(() => {
    if (open && document?.id) {
      loadPayments();
      setForm({ date: new Date().toISOString().split("T")[0], mode: "", amount_ttc: "", comment: "" });
      setEditingId(null);
      setChequeFile(null);
    }
  }, [open, document?.id, loadPayments]);

  const addPayment = async () => {
    if (!form.amount_ttc || toNum(form.amount_ttc) <= 0) {
      toast.error("Montant requis"); return;
    }
    setAdding(true);
    try {
      const res = await documentAPI.addPayment(document.id, {
        date: form.date,
        mode: newMode,
        amount_ttc: toNum(form.amount_ttc),
        comment: form.comment,
        cheque_image_base64: isChequeMode(newMode) ? (chequeFile?.base64 || "") : "",
        cheque_image_mime: isChequeMode(newMode) ? (chequeFile?.mime || "") : "",
      });
      setPayments(prev => [...prev, res.data.payment]);
      setTotalPaid(res.data.total_paid);
      setRemaining(res.data.remaining);
      if (res.data.status === "payee") {
        toast.success("Règlement enregistré - Facture marquée comme payée");
      } else {
        toast.success("Règlement enregistré");
      }
      setForm({ date: new Date().toISOString().split("T")[0], mode: "", amount_ttc: "", comment: "" });
      setChequeFile(null);
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setAdding(false); }
  };

  const deletePayment = async (paymentId) => {
    if (!window.confirm("Supprimer ce règlement ?")) return;
    try {
      const res = await documentAPI.deletePayment(document.id, paymentId);
      setPayments(prev => prev.filter(p => p.id !== paymentId));
      setTotalPaid(res.data.total_paid);
      setRemaining(res.data.remaining);
      toast.success("Règlement supprimé");
      onUpdated?.();
    } catch { toast.error("Erreur suppression"); }
  };

  // P3: Edit payment
  const startEdit = (p) => {
    setEditingId(p.id);
    setEditForm({ date: p.date || "", mode: p.mode || "", amount_ttc: p.amount_ttc?.toString() || "", comment: p.comment || "" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async (paymentId) => {
    if (!editForm.amount_ttc || toNum(editForm.amount_ttc) <= 0) {
      toast.error("Montant requis"); return;
    }
    try {
      const res = await documentAPI.updatePayment(document.id, paymentId, {
        date: editForm.date,
        mode: editForm.mode,
        amount_ttc: toNum(editForm.amount_ttc),
        comment: editForm.comment,
      });
      setPayments(prev => prev.map(p => p.id === paymentId ? res.data.payment : p));
      setTotalPaid(res.data.total_paid);
      setRemaining(res.data.remaining);
      toast.success("Règlement modifié");
      setEditingId(null);
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur modification");
    }
  };

  const fillRemaining = () => {
    setForm(prev => ({ ...prev, amount_ttc: remaining > 0 ? remaining.toFixed(2) : "" }));
  };

  const paidPercent = displayTotal > 0 ? Math.min(100, (totalPaid / displayTotal) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote size={18} className="text-emerald-600" />
            Règlements - {document?.number}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Invoice summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
            <div>
              <p className="text-[11px] text-slate-500 uppercase">Client</p>
              <p className="text-sm font-medium text-slate-800" data-testid="payment-client">{clientName || "-"}</p>
            </div>
            <div>
              <p className="text-[11px] text-slate-500 uppercase">{isSolde ? "Reste à facturer" : "Montant facture"}</p>
              <p className="text-sm font-bold text-slate-900" data-testid="payment-total">{formatEur(displayTotal)}</p>
              {isSolde && document?.total_ttc != null && (
                <p className="text-[10px] text-slate-400 mt-0.5" data-testid="payment-total-hint">Total devis&nbsp;: {formatEur(document.total_ttc)} · acomptes déduits</p>
              )}
            </div>
            <div>
              <p className="text-[11px] text-slate-500 uppercase">Restant dû</p>
              <p className={`text-sm font-bold ${remaining > 0 ? "text-red-600" : "text-emerald-600"}`} data-testid="payment-remaining">{formatEur(remaining)}</p>
              {(avoirInfo.avoir_imputed_ttc || 0) > 0 && (
                <p className="text-[11px] text-rose-600" data-testid="payment-avoir-imputed">Avoir imputé −{formatEur(avoirInfo.avoir_imputed_ttc)}</p>
              )}
              {(avoirInfo.avoir_draft_count || 0) > 0 && (
                <p className="text-[11px] text-amber-600" data-testid="payment-avoir-draft">
                  {avoirInfo.avoir_draft_count} avoir{avoirInfo.avoir_draft_count > 1 ? "s" : ""} en brouillon ({formatEur(avoirInfo.avoir_draft_ttc)}) — non imputé{avoirInfo.avoir_draft_count > 1 ? "s" : ""}
                </p>
              )}
              {(avoirInfo.avoir_refund_count || 0) > 0 && (
                <p className="text-[11px] text-slate-500" data-testid="payment-avoir-refund">
                  Avoir en remboursement ({formatEur(avoirInfo.avoir_refund_ttc)}) — non imputé sur cette facture
                </p>
              )}
            </div>
          </div>

          {/* Progress bar */}
          <div>
            <div className="flex justify-between text-xs text-slate-500 mb-1">
              <span>Réglé : {formatEur(totalPaid)}</span>
              <span>{paidPercent.toFixed(0)}%</span>
            </div>
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${paidPercent >= 100 ? "bg-emerald-500" : paidPercent > 0 ? "bg-indigo-500" : ""}`}
                style={{ width: `${paidPercent}%` }}
                data-testid="payment-progress"
              />
            </div>
          </div>

          {/* Add payment form */}
          <div className="border border-slate-200 rounded-lg p-3 bg-white">
            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Ajouter un règlement</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div>
                <Label className="text-[11px] text-slate-500">Date</Label>
                <DateField value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="h-8 text-sm" data-testid="payment-date" />
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">Mode règlement</Label>
                <select
                  value={newMode}
                  onChange={e => setForm(f => ({ ...f, mode: e.target.value }))}
                  className="w-full h-8 text-sm border border-slate-200 rounded px-2 bg-white"
                  data-testid="payment-mode"
                >
                  {pm.options.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">Montant TTC</Label>
                <div className="relative">
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="0,00"
                    value={form.amount_ttc}
                    onChange={e => setForm(f => ({ ...f, amount_ttc: e.target.value }))}
                    className="h-8 text-sm pr-12"
                    data-testid="payment-amount"
                  />
                  {remaining > 0 && (
                    <button
                      onClick={fillRemaining}
                      className="absolute right-1 top-1/2 -translate-y-1/2 text-[10px] text-indigo-600 hover:text-indigo-800 bg-indigo-50 px-1.5 py-0.5 rounded"
                      title="Remplir le solde restant"
                      data-testid="fill-remaining-btn"
                    >
                      Solde
                    </button>
                  )}
                </div>
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">Commentaire</Label>
                <Input
                  placeholder="Optionnel"
                  value={form.comment}
                  onChange={e => setForm(f => ({ ...f, comment: e.target.value }))}
                  className="h-8 text-sm"
                  data-testid="payment-comment"
                />
              </div>
            </div>
            {isChequeMode(newMode) && (
              <ChequeUpload chequeFile={chequeFile} setChequeFile={setChequeFile} />
            )}
            <Button onClick={addPayment} disabled={adding} size="sm" className="mt-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8" data-testid="add-payment-btn">
              {adding ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Plus size={14} className="mr-1" />}
              Enregistrer le règlement
            </Button>
          </div>

          {/* Payments table */}
          <div>
            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Details règlements</p>
            {loading ? (
              <div className="flex justify-center py-4"><Loader2 className="animate-spin h-5 w-5 text-slate-400" /></div>
            ) : payments.length === 0 ? (
              <div className="text-center py-6 text-sm text-slate-400">Aucun règlement enregistré</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs uppercase text-slate-500">Date</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500">Mode</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500 text-right">Montant TTC</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500">Commentaire</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500 w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map(p => (
                    <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                      {editingId === p.id ? (
                        <>
                          <TableCell className="p-1">
                            <DateField value={editForm.date} onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))} className="h-7 text-xs" data-testid="edit-payment-date" />
                          </TableCell>
                          <TableCell className="p-1">
                            <select value={editForm.mode} onChange={e => setEditForm(f => ({ ...f, mode: e.target.value }))} className="h-7 text-xs border border-slate-200 rounded px-1 bg-white w-full" data-testid="edit-payment-mode">
                              {!editForm.mode && <option value="">—</option>}
                              {pm.optionsWith(editForm.mode).map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                          </TableCell>
                          <TableCell className="p-1">
                            <Input type="text" inputMode="decimal" value={editForm.amount_ttc} onChange={e => setEditForm(f => ({ ...f, amount_ttc: e.target.value }))} className="h-7 text-xs text-right" data-testid="edit-payment-amount" />
                          </TableCell>
                          <TableCell className="p-1">
                            <Input value={editForm.comment} onChange={e => setEditForm(f => ({ ...f, comment: e.target.value }))} className="h-7 text-xs" placeholder="Optionnel" data-testid="edit-payment-comment" />
                          </TableCell>
                          <TableCell className="p-1">
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-600" onClick={() => saveEdit(p.id)} data-testid="save-edit-btn">
                                <Check size={13} />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400" onClick={cancelEdit} data-testid="cancel-edit-btn">
                                <X size={13} />
                              </Button>
                            </div>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell className="text-sm">{p.date ? new Date(p.date + "T00:00:00").toLocaleDateString("fr-FR") : "-"}</TableCell>
                          <TableCell className="text-sm" data-testid={`payment-mode-label-${p.id}`}>{pm.label(p.mode) || "-"}</TableCell>
                          <TableCell className="text-sm font-medium text-right">{formatEur(p.amount_ttc)}</TableCell>
                          <TableCell className="text-sm text-slate-500">{p.comment || "-"}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {p.has_cheque_image && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-indigo-600"
                                  title="Voir le chèque scanné"
                                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${document.id}/payments/${p.id}/cheque-image`, '_blank')}
                                  data-testid={`view-cheque-img-${p.id}`}
                                >
                                  <Paperclip size={13} />
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-indigo-500" onClick={() => startEdit(p)} data-testid={`edit-payment-${p.id}`}>
                                <Pencil size={13} />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => deletePayment(p.id)} data-testid={`delete-payment-${p.id}`}>
                                <Trash2 size={13} />
                              </Button>
                            </div>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                  <TableRow className="bg-slate-50 font-semibold">
                    <TableCell colSpan={2} className="text-sm">Total</TableCell>
                    <TableCell className="text-sm text-right">{formatEur(totalPaid)}</TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
